import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Labels } from 'src/app/app.models';
import { BankAccountData, BankAccountMode, BankForm, BankGFFPField, GetBankAccountsResponse, GetBankGFFPParams, NewBankAccount } from './bank-account.models';
import { Store } from '@ngrx/store';
import { selectAvailableBankAccounts, selectBankGFFPFields, selectDestinationCountryData, selectPaymentOption, selectSelectedBankAccount, SetSelectedBankAccount } from 'src/app/store/transaction';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { selectDigitalProfileID } from 'src/app/store/auth/auth.selectors';
import { AddNewBankAccount, GetBankAccounts, GetBankGFFPFields } from 'src/app/store/digital-payment/digital-payment.action';
import { selectFeeQuoteState } from 'src/app/store/fee-quote';
import { selectLocale } from 'src/app/store/query-params';

@Component({
    selector: 'app-bank-account',
    templateUrl: './bank-account.component.html',
    styleUrls: ['./bank-account.component.scss'],
    standalone: false
})
export class BankAccountComponent {

  @Output() bankSendValidateEvent: EventEmitter<void> = new EventEmitter<void>()
  @Output() updateBankModeEvent: EventEmitter<BankAccountMode> = new EventEmitter<BankAccountMode>()

  @Input() labels: Labels
  @Input() firebaseAuthToken: string
  @Input() paymentMethod: string
  @Input() bankMode: BankAccountMode

  private destroy$: Subject<void> = new Subject<void>()

  public optionConfig: any = null
  public selectedBankAccount: any = null
  public digitalProfileId: string
  private bankGFFPParams: GetBankGFFPParams = {
    destinationCountryCode: '',
    destinationCountrySubdivisionCode: '',
    sendAmount: '',
    sendCurrencyCode: '',
    receiveCurrencyCode: '',
    serviceOptionCode: '',
    serviceOptionRoutingCode: ''
  }
  public bankGFFPFields: BankGFFPField[] = []

  constructor(
    private store: Store<any>,
  ) {}

  ngOnInit() {
    this.store.select(selectDigitalProfileID)
      .pipe(takeUntil(this.destroy$))
      .subscribe(digitalProfileId => {
        this.digitalProfileId = digitalProfileId
    })

    this.store.select(selectAvailableBankAccounts)
      .pipe(takeUntil(this.destroy$))
      .subscribe(availableBankAccounts => {
        if (availableBankAccounts) {
          this.optionConfig = this.buildConfigObject(availableBankAccounts)
          this.changeBankMode('select')
        }
    })

    this.store.select(selectSelectedBankAccount)
      .pipe(takeUntil(this.destroy$)) 
      .subscribe(selectedBankAccount => {
        this.selectedBankAccount = selectedBankAccount
    })

    this.store.select(selectDestinationCountryData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(destinationCountryData => {
        this.bankGFFPParams['destinationCountryCode'] = destinationCountryData.country! // Todo: Tim: is asserting that these are not null the best approach??
        this.bankGFFPParams['destinationCountrySubdivisionCode'] = destinationCountryData.state ?? ''
        this.bankGFFPParams['sendAmount'] = destinationCountryData.amount!
        this.bankGFFPParams['receiveCurrencyCode'] = destinationCountryData.currency!
    })

    this.store.select(selectPaymentOption)
      .pipe(takeUntil(this.destroy$))
      .subscribe(paymentOption => {
        this.bankGFFPParams['serviceOptionCode'] = paymentOption?.serviceOptionData?.payoutOptionCode ? paymentOption.serviceOptionData.payoutOptionCode : ''
        this.bankGFFPParams['serviceOptionRoutingCode'] = paymentOption?.serviceOptionRoutingCode ? paymentOption.serviceOptionRoutingCode : ''
    })

    this.store.select(selectLocale)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userLanguage => {
        this.bankGFFPParams['userLanguage'] = userLanguage
    })

    this.store.select(selectFeeQuoteState)
      .pipe(takeUntil(this.destroy$))
      .subscribe(feeQuote => {
        this.bankGFFPParams['sendCurrencyCode'] = feeQuote.transactions[0].sendAmountDetails.amount?.currencyCode ? feeQuote.transactions[0].sendAmountDetails.amount.currencyCode : ''
    })

    this.store.select(selectBankGFFPFields)
      .pipe(takeUntil(this.destroy$))
      .subscribe(bankGFFPFields => {
        if (bankGFFPFields) {
          this.bankGFFPFields = bankGFFPFields
          this.changeBankMode('add')
        }
    })

    if (this.paymentMethod === 'bank') {
      this.store.dispatch(GetBankAccounts({ digitalProfileId: this.digitalProfileId, firebaseAuthToken: this.firebaseAuthToken }))
    }
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  public changeBankMode(mode: BankAccountMode): void {
    if (mode === 'add' && this.bankGFFPFields.length < 1) {
      this.store.dispatch(GetBankGFFPFields(this.bankGFFPParams))
    }
    else {
      this.updateBankModeEvent.emit(mode)
    }
  }

  public changeSelectedBank(selectedBankAccount: any): void {
    this.selectedBankAccount = selectedBankAccount
    this.store.dispatch(SetSelectedBankAccount({ selectedBankAccount: this.selectedBankAccount}))
  }

  public emitBankSendValidateEvent(): void {
    this.bankSendValidateEvent.emit()
  }

  private buildConfigObject(bankAccounts: BankAccountData[]): any { // Todo: Type
    const optionConfig = bankAccounts.map(bankAccount => {
      return {
        id: bankAccount.targetAccountProfileId,
        icon: 'assets/images/payment-method-icons/payment-method-bank-icon.svg',
        iconAltText: 'Bank Icon',
        title: bankAccount.bankName,
        subtitle: bankAccount.accountNumberMasked,
        optionMetadata: bankAccount,
      }
    })
    return optionConfig
  }

  public addNewBankAccount(bankAccount: BankForm): void { // Todo: Type
    const newBankAccount: NewBankAccount  = {
      newBankAccountInfo: {
        ...bankAccount
      },
      bankName: '',
      targetAccountProfileId: `newaccount-${bankAccount.targetAccountAccountNumber}`,
      accountNumberMasked: this.maskAccountNumber(bankAccount.targetAccountAccountNumber)
    }
    this.store.dispatch(AddNewBankAccount(newBankAccount))
  }

  public maskAccountNumber(accountNumber: string): string {
    return '****' + accountNumber.slice(-4);
  }
}
